// MHM-3.5 — Unified shipment-detail surface for the MHM portal.
// Used both as a slide-in drawer and as a standalone page at
// /dashboard/hub/orders/:tracking_id (via OrderDetailPage).
//
// Layout: sticky header (status chip + tracking id + actions) + 5 tabs:
//   Overview · Timeline · Handling Chain · Proofs · Audit
//
// Loading uses skeleton blocks (NEVER an infinite "Loading…").
// 403 / 404 / network errors render a clean card with Retry.
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { X, ExternalLink, Copy, AlertOctagon, ChevronRight, RefreshCw, CheckCircle2, ZoomIn, Camera, Upload, Inbox, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import StatusBadge from "./StatusBadge";
import { useOrderDrawer } from "./OrderDrawerContext";
import CourierTab from "./CourierTab";

const TABS = [
  { key: "overview",  label: "Overview" },
  { key: "custody",   label: "Custody" },
  { key: "timeline",  label: "Timeline" },
  { key: "chain",     label: "Handling Chain" },
  { key: "courier",   label: "Courier" },
  { key: "proofs",    label: "Proofs" },
  { key: "audit",     label: "Audit" },
];

const STATUS_CHIP = {
  delivered: "bg-green-100 text-green-800 border-green-200",
  rto: "bg-rose-100 text-rose-800 border-rose-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  out_for_delivery: "bg-sky-100 text-sky-800 border-sky-200",
  in_transit: "bg-violet-100 text-violet-800 border-violet-200",
  picked_up: "bg-amber-100 text-amber-800 border-amber-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
};

function Skeleton({ className = "" }) {
  return <div className={"animate-pulse bg-slate-100 rounded " + className} />;
}

function Section({ title, children, testid }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white" data-testid={testid}>
      <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div className="flex items-start gap-3 py-1">
      <span className="text-xs text-slate-500 w-32 shrink-0">{label}</span>
      <span className={"text-sm text-slate-800 break-all " + (mono ? "font-mono" : "")}>
        {value || <span className="text-slate-300">—</span>}
      </span>
    </div>
  );
}

function HumanDateTime({ iso }) {
  if (!iso) return <span className="text-slate-300">—</span>;
  try {
    const d = new Date(iso);
    return <span title={iso}>{d.toLocaleString()}</span>;
  } catch {
    return iso;
  }
}

function StatusChip({ status, label }) {
  const cls = STATUS_CHIP[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}
          data-testid="order-detail-status-chip">
      {label || status}
    </span>
  );
}

// MHM-4 · AI delay-prediction chip surfaced inside the Overview tab.
const RISK_STYLE = {
  low:    "bg-green-50 text-green-800 border-green-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  high:   "bg-rose-50 text-rose-800 border-rose-200",
};

function AiDelayChip({ tid }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/hub/orders/${tid}/delay-prediction`);
        if (alive) setD(data);
      } catch (e) {
        const code = e?.response?.status;
        if (alive) setErr(code === 503 ? "AI disabled" : (e.userMessage || "AI unavailable"));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [tid]);
  if (loading) {
    return (
      <div className="border border-slate-200 rounded-xl p-3 bg-white animate-pulse text-xs text-slate-400"
           data-testid="ai-delay-chip-loading">AI delay analysis loading…</div>
    );
  }
  if (err) {
    return (
      <div className="border border-slate-200 rounded-xl p-3 bg-white text-xs text-slate-400"
           data-testid="ai-delay-chip-disabled">AI delay analysis · {err}</div>
    );
  }
  const cls = RISK_STYLE[d.risk] || RISK_STYLE.medium;
  return (
    <div className={`border rounded-xl p-3 ${cls}`} data-testid="ai-delay-chip">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wider font-semibold">AI delay risk</span>
          <span className="px-2 py-0.5 rounded-full bg-white/60 font-bold text-sm" data-testid="ai-delay-chip-risk">
            {String(d.risk || "").toUpperCase()}
          </span>
          <span className="text-xs opacity-70">· {d.confidence_pct ?? 0}% conf</span>
          {d.cached && <span className="text-[10px] uppercase opacity-60">cached</span>}
        </div>
      </div>
      {(d.factors || []).length > 0 && (
        <div className="mt-2 text-xs opacity-90">
          {(d.factors || []).slice(0, 3).map((f, i) => (
            <span key={i} className="inline-block bg-white/60 rounded px-1.5 py-0.5 mr-1 mb-1">{f}</span>
          ))}
        </div>
      )}
      {d.recommended_action && (
        <div className="mt-1 text-xs italic opacity-90">"{d.recommended_action}"</div>
      )}
    </div>
  );
}

// ── Tab content components ──────────────────────────────────────────

function DeliveredBadge({ created_at, delivered_at, picked_up_at }) {
  if (!delivered_at) {
    return (
      <span data-testid="overview-delivered-badge-pending"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
        Awaiting delivery
      </span>
    );
  }
  // Compute duration since pickup (or created if pickup missing)
  const base = picked_up_at || created_at;
  let dur = "";
  if (base) {
    const ms = new Date(delivered_at) - new Date(base);
    if (Number.isFinite(ms) && ms > 0) {
      const m = Math.floor(ms / 60000);
      const h = Math.floor(m / 60);
      const remM = m % 60;
      const d = Math.floor(h / 24);
      const remH = h % 24;
      dur = d > 0 ? `${d}d ${remH}h ${remM}m`
          : h > 0 ? `${h}h ${remM}m`
          : `${m}m`;
    }
  }
  const local = new Date(delivered_at).toLocaleString();
  return (
    <span data-testid="overview-delivered-badge"
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
      <CheckCircle2 className="h-3.5 w-3.5" />
      Delivered · {local}{dur && <span className="opacity-70">· {dur} since pickup</span>}
    </span>
  );
}

function OverviewTab({ d }) {
  const o = d.order; const cu = d.customer || {};
  return (
    <div className="space-y-3" data-testid="order-detail-tab-overview-body">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <AiDelayChip tid={o.tracking_id} />
        <DeliveredBadge created_at={o.created_at}
                        delivered_at={o.delivered_at}
                        picked_up_at={(d.pickup_agent || {}).picked_up_at} />
      </div>
      <Section title="Order summary">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Tracking ID" value={o.tracking_id} mono />
          <Field label="Status" value={o.current_status_label} />
          <Field label="Created" value={<HumanDateTime iso={o.created_at} />} />
          <Field label="Delivered" value={<HumanDateTime iso={o.delivered_at} />} />
          <Field label="Mode" value={o.delivery_mode} />
          <Field label="Service" value={o.service_type} />
          <Field label="ETA" value={<HumanDateTime iso={o.estimated_delivery} />} />
          <Field label="Rating" value={o.rating != null ? `${o.rating} ★` : null} />
        </div>
      </Section>
      <Section title="Customer">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Name" value={cu.name} />
          <Field label="Email" value={cu.email_masked} mono />
          <Field label="Phone" value={cu.phone_masked} mono />
          <Field label="User type" value={cu.user_type} />
        </div>
      </Section>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Section title="Pickup address" testid="order-detail-pickup-addr">
          <Field label="Contact" value={cu.pickup_address?.contact_name} />
          <Field label="Phone" value={cu.pickup_address?.contact_phone_masked} mono />
          <Field label="Address" value={[cu.pickup_address?.line1, cu.pickup_address?.line2].filter(Boolean).join(", ")} />
          <Field label="City · PIN" value={`${cu.pickup_address?.city || "—"} · ${cu.pickup_address?.pincode || "—"}`} />
          <Field label="State" value={cu.pickup_address?.state} />
        </Section>
        <Section title="Drop address" testid="order-detail-drop-addr">
          <Field label="Contact" value={cu.delivery_address?.contact_name} />
          <Field label="Phone" value={cu.delivery_address?.contact_phone_masked} mono />
          <Field label="Address" value={[cu.delivery_address?.line1, cu.delivery_address?.line2].filter(Boolean).join(", ")} />
          <Field label="City · PIN" value={`${cu.delivery_address?.city || "—"} · ${cu.delivery_address?.pincode || "—"}`} />
          <Field label="State" value={cu.delivery_address?.state} />
        </Section>
      </div>
      <Section title={`Parcels (${o.parcels.length})`}>
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 text-left">
            <tr><th className="py-1">Name</th><th>Weight</th><th>Dims</th><th>Value</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {o.parcels.map((p) => (
              <tr key={p.parcel_id}>
                <td className="py-1.5">{p.name}</td>
                <td>{p.weight_kg} kg</td>
                <td className="font-mono text-xs">{p.dim_lxbxh_cm}</td>
                <td>₹{p.declared_value || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Section title="Payment + value">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Method" value={(o.payment?.method || "").toUpperCase()} />
          <Field label="Status" value={o.payment?.status} />
          <Field label="Subtotal" value={`₹${o.value?.subtotal || 0}`} />
          <Field label="Discount" value={o.coupon ? `−₹${o.coupon.discount_amount} (${o.coupon.code})` : "—"} />
          <Field label="COD fee" value={`₹${o.value?.cod_fee || 0}`} />
          <Field label="Final" value={`₹${o.value?.final || 0}`} />
          {o.payment?.cod_pending && (
            <Field label="COD pending" value={<span className="text-amber-700">Yes · ₹{o.payment?.amount_due}</span>} />)}
          {o.payment?.cod_collected_amount > 0 && (
            <Field label="COD collected" value={`₹${o.payment?.cod_collected_amount}`} />)}
        </div>
      </Section>
    </div>
  );
}

function TimelineTab({ d }) {
  const tl = d.timeline || [];
  if (tl.length === 0) {
    return <div className="text-sm text-slate-500 italic py-6 text-center" data-testid="order-detail-tab-timeline-empty">
      No timeline events recorded yet.
    </div>;
  }
  return (
    <ol className="space-y-3" data-testid="order-detail-tab-timeline-body">
      {tl.map((e, i) => (
        <li key={i} className="flex gap-3">
          <div className="flex flex-col items-center">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF6B00] mt-2"></span>
            {i < tl.length - 1 && <span className="w-px flex-1 bg-orange-100 mt-1" />}
          </div>
          <div className="flex-1 pb-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-800">{e.label}</div>
            <div className="text-xs text-slate-500">
              <HumanDateTime iso={e.at} />
              {e.actor_role && <span className="ml-2">· {e.actor_role}{e.actor_name && `: ${e.actor_name}`}</span>}
            </div>
            {e.meta?.note && <div className="text-xs text-slate-600 mt-1 italic">"{e.meta.note}"</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function PackerAssignment({ tid, mh, currentUserRole, micro_hub_id, onChanged, readOnly = false }) {
  // Show / assign / unassign the packer worker.
  // Edit allowed only for micro_hub_lead (in scope) or admin/founder/ceo.
  // MHM-6.D fix · `readOnly` collapses the picker + unassign affordance —
  // used by the legacy ChainTab so the Custody tab stays the single
  // canonical place to assign/unassign the packer.
  const [workers, setWorkers] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [showUnassign, setShowUnassign] = useState(false);
  const [reason, setReason] = useState("");
  const ps = mh?.packing_staff;
  const canEdit = !readOnly
    && ["micro_hub_lead", "admin", "founder", "ceo"].includes(currentUserRole);

  const loadWorkers = useCallback(async () => {
    if (!canEdit || ps || !micro_hub_id) return;
    try {
      const { data } = await api.get(`/micro-hub/workers?role=packer&is_active=true&micro_hub_id=${micro_hub_id}&limit=200`);
      setWorkers(data.rows || []);
    } catch (e) {
      // 403 = out of scope; just hide the picker
      setWorkers([]);
    }
  }, [canEdit, ps, micro_hub_id]);
  useEffect(() => { loadWorkers(); }, [loadWorkers]);

  const assign = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await api.post(`/micro-hub/orders/${tid}/assign-packer`, { worker_id: selectedId });
      toast.success("Packer assigned");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Assign failed");
    } finally { setBusy(false); }
  };

  const unassign = async () => {
    if (!reason) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/micro-hub/orders/${tid}/unassign-packer`, { reason });
      toast.success("Packer unassigned");
      setShowUnassign(false); setReason("");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Unassign failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 pt-3 border-t border-slate-100" data-testid="chain-packer-section">
      <div className="text-xs font-semibold uppercase tracking-wider text-[#C2410C] mb-2">
        Packer assignment
      </div>
      {ps ? (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3" data-testid="chain-packer-assigned">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Field label="Packer" value={ps.name} />
            <Field label="Phone" value={ps.phone_masked} mono />
            <Field label="Assigned at" value={<HumanDateTime iso={ps.assigned_at} />} />
            <Field label="Status" value={ps.packing_status || "assigned"} />
            {ps.notes && <Field label="Notes" value={ps.notes} />}
          </div>
          {canEdit && !showUnassign && (
            <button onClick={() => setShowUnassign(true)} data-testid="chain-packer-unassign-toggle"
                    className="mt-2 text-xs text-rose-600 hover:underline">
              Unassign packer
            </button>
          )}
          {canEdit && showUnassign && (
            <div className="mt-3 space-y-2" data-testid="chain-packer-unassign-form">
              <input value={reason} onChange={(e) => setReason(e.target.value)}
                     placeholder="Reason (required)"
                     data-testid="chain-packer-unassign-reason"
                     className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
              <div className="flex gap-2">
                <button onClick={() => { setShowUnassign(false); setReason(""); }}
                        className="text-xs px-3 py-1 border border-slate-200 rounded-lg">Cancel</button>
                <button onClick={unassign} disabled={busy || !reason}
                        data-testid="chain-packer-unassign-confirm"
                        className="text-xs px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg disabled:opacity-60">
                  {busy ? "Working…" : "Confirm unassign"}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : canEdit ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="chain-packer-picker">
          {workers === null && <p className="text-xs text-slate-400">Loading packers…</p>}
          {workers && workers.length === 0 && (
            <p className="text-xs text-slate-500">
              No active packers in this micro hub.{" "}
              <Link to="/dashboard/micro-hub/workers?new=1" className="text-[#FF6B00] hover:underline">
                Add one →
              </Link>
            </p>
          )}
          {workers && workers.length > 0 && (
            <div className="flex gap-2 items-center">
              <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}
                      data-testid="chain-packer-select"
                      className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
                <option value="">Select a packer…</option>
                {workers.map((w) => (
                  <option key={w.worker_id} value={w.worker_id}>
                    {w.name} · {w.phone_masked}
                  </option>
                ))}
              </select>
              <button onClick={assign} disabled={busy || !selectedId}
                      data-testid="chain-packer-assign-btn"
                      className="text-sm px-3 py-1.5 bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg disabled:opacity-60">
                {busy ? "Assigning…" : "Assign"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic">No packer assigned yet.</p>
      )}
    </div>
  );
}

function ChainTab({ d, onChanged }) {
  const pa = d.pickup_agent; const da = d.delivery_agent;
  const mh = d.micro_hub; const main = d.main_hub;
  const man = d.dispatch_manifest; const cou = d.courier_details;
  // MHM-5.4 patch — read role from canonical pickngo_user key (was "user", always missing).
  // That bug meant `canEdit` resolved to false for everyone, hiding the packer dropdown.
  const role = (typeof window !== "undefined" &&
                JSON.parse(localStorage.getItem("pickngo_user") || "{}").role) || "";
  return (
    <div className="space-y-3" data-testid="order-detail-tab-chain-body">
      <Section title="1 · Pickup agent" testid="chain-pickup-agent">
        {pa ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Field label="Name" value={pa.name} />
            <Field label="Phone" value={pa.phone_masked} mono />
            <Field label="Vehicle" value={pa.vehicle_type} />
            <Field label="Rating" value={pa.rating_avg != null ? `${pa.rating_avg} ★` : null} />
            <Field label="Assigned at" value={<HumanDateTime iso={pa.assigned_at} />} />
            <Field label="Accepted at" value={<HumanDateTime iso={pa.accepted_at} />} />
            <Field label="Picked at" value={<HumanDateTime iso={pa.picked_up_at} />} />
            <Field label="Pickup OTP" value={pa.pickup_otp?.verified ? "✓ Verified" : "Pending"} />
            <Field label="Geo" value={pa.pickup_geo ? `${pa.pickup_geo.lat}, ${pa.pickup_geo.lng}` : null} mono />
            <Field label="Remarks" value={pa.remarks} />
          </div>
        ) : <p className="text-sm text-slate-400 italic">No agent assigned yet.</p>}
      </Section>

      <Section title="2 · Micro hub" testid="chain-micro-hub">
        {mh ? (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Field label="Hub" value={`${mh.name} (${mh.hub_id})`} />
            <Field label="Address" value={mh.address} />
            <Field label="Lead" value={mh.lead_name} />
            <Field label="Lead phone" value={mh.lead_phone_masked} mono />
            <Field label="Received at" value={<HumanDateTime iso={mh.received_at} />} />
            <Field label="Packed at" value={<HumanDateTime iso={mh.packed_at} />} />
            <Field label="Dispatched to main" value={<HumanDateTime iso={mh.dispatched_to_main_at} />} />
            <Field label="Post-pack weight" value={mh.post_pack_weight_kg ? `${mh.post_pack_weight_kg} kg` : null} />
            <Field label="Packing material" value={(mh.packing_material || []).join(", ") || null} />
            <Field label="Remarks" value={mh.remarks} />
          </div>
          <PackerAssignment tid={d.order.tracking_id} mh={mh}
                            currentUserRole={role}
                            micro_hub_id={mh.hub_id}
                            readOnly={true}
                            onChanged={onChanged}/>
          </>
        ) : <p className="text-sm text-slate-400 italic">Not routed through a micro hub.</p>}
      </Section>

      <Section title="3 · Main hub" testid="chain-main-hub">
        {main ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Field label="Hub" value={`${main.name} (${main.hub_id})`} />
            <Field label="Address" value={main.address} />
            <Field label="Received at" value={<HumanDateTime iso={main.received_at} />} />
            <Field label="Scan verified" value={main.scan_verification?.verified ? "✓ Yes" : "Pending"} />
            <Field label="Manifest ID" value={main.dispatch_manifest_id} mono />
            <Field label="Courier" value={main.courier_partner ? main.courier_partner.toUpperCase() : null} />
            <Field label="AWB (mock)" value={main.awb_tracking_number} mono />
            <Field label="Outgoing at" value={<HumanDateTime iso={main.outgoing_dispatch_at} />} />
          </div>
        ) : <p className="text-sm text-slate-400 italic">Not yet at a main hub.</p>}
      </Section>

      <Section title="4 · Outbound dispatch & courier" testid="chain-courier">
        {man || cou ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Field label="Manifest" value={man?.name || man?.manifest_id} mono />
            <Field label="Vehicle" value={man?.vehicle?.vehicle_no} mono />
            <Field label="Driver" value={man?.vehicle?.driver_name} />
            <Field label="Status" value={man?.status} />
            <Field label="Total parcels" value={man?.total_parcels} />
            <Field label="Total weight" value={man?.total_weight_kg ? `${man.total_weight_kg} kg` : null} />
            <Field label="Partner" value={cou?.display_name} />
            <Field label="SLA class" value={cou?.sla_class} />
            <Field label="Price estimate" value={cou?.price_estimate ? `₹${cou.price_estimate}` : null} />
            <Field label="ETA" value={<HumanDateTime iso={cou?.estimated_delivery_at} />} />
            <Field label="Tracking URL" value={cou?.tracking_url_mock
              ? <a href={cou.tracking_url_mock} target="_blank" rel="noopener noreferrer" className="text-sky-600 underline break-all">{cou.tracking_url_mock}</a>
              : null} />
          </div>
        ) : <p className="text-sm text-slate-400 italic">Not yet manifested.</p>}
      </Section>

      {da && (
        <Section title="5 · Last-mile delivery agent" testid="chain-delivery-agent">
          {da.same_as_pickup_agent && (
            <div className="mb-2 text-xs bg-blue-50 border border-blue-200 text-blue-700 rounded px-2 py-1 inline-block"
                 data-testid="chain-da-same-as-pickup">
              Same as pickup agent
            </div>
          )}
          {!da.delivered_at && (
            <div className="mb-2 text-xs bg-slate-100 border border-slate-200 text-slate-600 rounded px-2 py-1 inline-block"
                 data-testid="chain-da-awaiting">
              — Delivery not yet completed
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Field label="Name" value={da.name || "—"} />
            <Field label="Phone" value={da.phone_masked} mono />
            <Field label="Vehicle" value={da.vehicle_type} />
            <Field label="Accepted at" value={<HumanDateTime iso={da.accepted_at} />} />
            <Field label="Delivered at" value={<HumanDateTime iso={da.delivered_at} />} />
            <Field label="Recipient" value={da.recipient_name} />
            <Field label="Delivery OTP" value={da.delivery_otp?.verified ? "✓ Verified" : "Pending"} />
            <Field label="Geo" value={da.delivery_geo ? `${da.delivery_geo.lat}, ${da.delivery_geo.lng}` : null} mono />
            {da.remarks && <Field label="Remarks" value={da.remarks} />}
          </div>
        </Section>
      )}
    </div>
  );
}

// MHM-5.4-r5 — Lightbox + 4-section Proofs gallery
function PhotoTile({ src, label, meta, onClick, testid }) {
  if (!src) return null;
  return (
    <button onClick={() => onClick(src, label, meta)} type="button"
            data-testid={testid}
            className="group relative inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00] rounded-lg">
      <img src={src} alt={label || "photo"}
           className="h-24 w-24 object-cover rounded-lg border border-slate-200 group-hover:border-[#FF6B00] transition-colors"/>
      <span className="absolute top-1 right-1 bg-black/40 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <ZoomIn className="h-3 w-3"/>
      </span>
      {label && <span className="block text-[10px] text-slate-500 mt-1 max-w-[96px] truncate">{label}</span>}
    </button>
  );
}

function Lightbox({ src, label, meta, onClose }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
         role="dialog" aria-modal="true" aria-label={label || "Photo"}
         data-testid="order-photo-lightbox"
         onClick={onClose}>
      <button onClick={onClose} aria-label="Close photo"
              data-testid="order-photo-lightbox-close"
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2">
        <X className="h-5 w-5"/>
      </button>
      <div className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={label || ""}
             className="max-w-full max-h-[80vh] rounded-lg border border-white/20" />
        {(label || meta) && (
          <div className="bg-white/5 text-white text-xs rounded-lg px-3 py-2 max-w-md">
            {label && <div className="font-semibold mb-0.5">{label}</div>}
            {meta && <div className="opacity-80">{meta}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function ProofsTab({ d }) {
  const cust = d.customer_uploads || [];
  const ag   = d.agent_uploads || [];
  const pak  = d.packer_uploads || [];
  const [lb, setLb] = useState(null);
  const openLb = (src, label, meta) => setLb({ src, label, meta });
  const closeLb = () => setLb(null);

  const pickup   = ag.filter((a) => a.type === "pickup");
  const delivery = ag.filter((a) => a.type === "delivery");

  const fmtAt = (iso) => iso ? new Date(iso).toLocaleString() : "—";
  const fmtBy = (u) => u ? `${u.name || "agent"}${u.role ? ` (${u.role})` : ""}` : "—";

  return (
    <div className="space-y-3" data-testid="order-detail-tab-proofs-body">
      {/* 1. Customer uploads */}
      <Section title={`1 · Customer uploads (${cust.length})`} testid="proofs-customer-uploads">
        {cust.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No customer photos uploaded.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {cust.map((c, i) => (
              <PhotoTile key={i} src={c.photo_b64}
                         label={c.parcel_id ? `Parcel ${c.parcel_id}` : (c.caption || "Customer photo")}
                         meta={`Uploaded ${fmtAt(c.uploaded_at)} · by customer`}
                         testid={`proofs-customer-tile-${i}`}
                         onClick={openLb}/>
            ))}
          </div>
        )}
      </Section>

      {/* 2. Agent uploads — Pickup */}
      <Section title={`2 · Agent uploads · Pickup (${pickup.length})`} testid="proofs-agent-pickup">
        {pickup.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No pickup proof photo yet.</p>
        ) : (
          <div className="space-y-2">
            {pickup.map((p, i) => (
              <div key={i} className="flex gap-3 items-start">
                <PhotoTile src={p.photo_b64} label="Pickup proof"
                           meta={`${fmtAt(p.uploaded_at)} · ${fmtBy(p.uploaded_by)}`}
                           testid={`proofs-pickup-tile-${i}`}
                           onClick={openLb}/>
                <div className="text-xs text-slate-600 space-y-0.5">
                  <div>By <span className="font-medium">{p.uploaded_by?.name || "—"}</span> · agent</div>
                  <div>At <span className="font-mono">{fmtAt(p.uploaded_at)}</span></div>
                  {p.geo && <div className="font-mono text-slate-500">Geo: {p.geo.lat}, {p.geo.lng}{p.geo.accuracy_m && ` (±${p.geo.accuracy_m}m)`}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 3. Agent uploads — Delivery */}
      <Section title={`3 · Agent uploads · Delivery (${delivery.length})`} testid="proofs-agent-delivery">
        {delivery.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No delivery proof photo yet.</p>
        ) : (
          <div className="space-y-2">
            {delivery.map((p, i) => (
              <div key={i} className="flex gap-3 items-start">
                <PhotoTile src={p.photo_b64} label="Delivery proof"
                           meta={`${fmtAt(p.uploaded_at)} · ${fmtBy(p.uploaded_by)}`}
                           testid={`proofs-delivery-tile-${i}`}
                           onClick={openLb}/>
                <div className="text-xs text-slate-600 space-y-0.5">
                  <div>By <span className="font-medium">{p.uploaded_by?.name || "—"}</span> · agent</div>
                  {p.recipient_name && <div>Recipient <span className="font-medium">{p.recipient_name}</span></div>}
                  <div>At <span className="font-mono">{fmtAt(p.uploaded_at)}</span></div>
                  {p.geo && <div className="font-mono text-slate-500">Geo: {p.geo.lat}, {p.geo.lng}{p.geo.accuracy_m && ` (±${p.geo.accuracy_m}m)`}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 4. Packer uploads */}
      <Section title={`4 · Packer uploads (${pak.length})`} testid="proofs-packer-uploads">
        {pak.length === 0 ? (
          <div className="text-sm text-slate-400 italic bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3"
               data-testid="proofs-packer-empty">
            No packing photos uploaded yet — packer portal coming soon.
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {pak.map((p, i) => (
              <PhotoTile key={i} src={p.photo_b64}
                         label={p.caption || (p.parcel_id ? `Parcel ${p.parcel_id}` : "Packing")}
                         meta={`${fmtAt(p.uploaded_at)} · by ${p.uploaded_by?.name || "packer"}`}
                         testid={`proofs-packer-tile-${i}`}
                         onClick={openLb}/>
            ))}
          </div>
        )}
      </Section>

      {lb && <Lightbox src={lb.src} label={lb.label} meta={lb.meta} onClose={closeLb} />}
    </div>
  );
}

function AuditTab({ d }) {
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState("");
  const rows = (d.audit_logs || []).filter((r) => !roleFilter || r.actor_role === roleFilter);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const slice = rows.slice((page - 1) * pageSize, page * pageSize);
  const roles = Array.from(new Set((d.audit_logs || []).map((r) => r.actor_role).filter(Boolean)));
  return (
    <div className="space-y-3" data-testid="order-detail-tab-audit-body">
      <Section title={`Audit log (${d.audit_logs?.length || 0} entries)`}>
        <div className="flex items-center gap-2 mb-2 text-xs">
          <span className="text-slate-500">Filter actor:</span>
          <select className="border border-slate-200 rounded px-2 py-1"
                  value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
            <option value="">All</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {slice.length === 0 ? (
          <p className="text-sm text-slate-400 italic py-3 text-center">No audit entries for this order yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-slate-500 text-left">
              <tr><th className="py-1">When</th><th>Actor</th><th>Action</th><th>After</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {slice.map((a, i) => (
                <tr key={i}>
                  <td className="py-1"><HumanDateTime iso={a.at} /></td>
                  <td>{a.actor_role || "—"}</td>
                  <td className="font-mono">{a.action}</td>
                  <td className="text-slate-600 break-all">{a.after_snippet || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div className="flex justify-end items-center gap-2 mt-2 text-xs">
            <button disabled={page === 1} onClick={() => setPage(page - 1)}
                    className="px-2 py-1 border rounded disabled:opacity-40">Prev</button>
            <span>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                    className="px-2 py-1 border rounded disabled:opacity-40">Next</button>
          </div>
        )}
      </Section>
      {d.notifications?.length > 0 && (
        <Section title={`Notifications (${d.notifications.length})`}>
          <ul className="text-xs space-y-1">
            {d.notifications.map((n, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-slate-500"><HumanDateTime iso={n.at} /></span>
                <span className="font-mono">{n.event}</span>
                <span className="text-slate-500">· {n.channel}</span>
                {n.success ? <span className="text-green-600">✓</span> : <span className="text-rose-600">✗</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}


// MHM-6.D · Chain-of-Custody tab — Pickup Agent / Packer / Delivery Agent /
// Handover to Main Hub / Main Hub Received / Audit Log, in chronological
// order. Role-aware affordances.
function ProofTile({ p, onZoom }) {
  return (
    <div className="relative group" data-testid={`proof-${p.id || p.url}`}>
      <button onClick={() => onZoom && onZoom(p.url)}
              className="block w-full aspect-square overflow-hidden rounded-lg
                         border border-slate-200 bg-slate-100 hover:opacity-90">
        <img src={p.url} alt="proof" className="w-full h-full object-cover" />
      </button>
      <div className="mt-1 text-[10px] text-slate-500 leading-tight">
        <div className="truncate" title={p.uploaded_by_name}>
          {p.uploaded_by_name || p.uploaded_by_role || "Unknown"}
        </div>
        <div className="text-slate-400">{p.uploaded_by_role || "—"}</div>
        {p.remarks && (
          <div className="mt-0.5 italic truncate" title={p.remarks}>“{p.remarks}”</div>
        )}
      </div>
    </div>
  );
}

function ProofGallery({ photos, label, testid }) {
  const [zoom, setZoom] = useState(null);
  if (!photos || photos.length === 0) {
    return (
      <p className="text-xs text-slate-400" data-testid={`${testid}-empty`}>
        No {label} yet.
      </p>
    );
  }
  return (
    <>
      <div className="grid grid-cols-3 gap-2" data-testid={testid}>
        {photos.map((p, i) => (
          <ProofTile key={p.id || p.url || i} p={p} onZoom={setZoom} />
        ))}
      </div>
      {zoom && (
        <button onClick={() => setZoom(null)}
                className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center"
                data-testid="proof-lightbox">
          <img src={zoom} alt="proof" className="max-w-[95vw] max-h-[90vh] object-contain" />
        </button>
      )}
    </>
  );
}

function AgentAssignBox({ d, tid, kind, role, onChanged }) {
  const block = d?.[`${kind}_agent`];
  const [open, setOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);
  const isLead = role === "micro_hub_lead";
  const status = block?.status;
  const completed = (kind === "pickup" && status === "picked")
                    || (kind === "delivery" && status === "delivered");

  const fetchAgents = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/micro-hub/agents?capability=${kind}`);
      setAgents(data?.rows || []);
    } catch (e) {
      toast.error(e.userMessage || `Failed to load ${kind} agents`);
    } finally { setBusy(false); }
  };

  const doAssign = async (agent_user_id) => {
    setBusy(true);
    try {
      await api.post(`/micro-hub/orders/${tid}/assign-${kind}-agent`,
                      { agent_user_id });
      toast.success(`${kind === "pickup" ? "Pickup" : "Delivery"} agent assigned`);
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Assign failed");
    } finally { setBusy(false); }
  };

  const doUnassign = async () => {
    if (!isLead || !window.confirm(`Unassign ${kind} agent?`)) return;
    setBusy(true);
    try {
      await api.post(`/micro-hub/orders/${tid}/unassign-${kind}-agent`);
      toast.success("Unassigned");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Unassign failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`custody-${kind}-section`} className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="font-semibold text-slate-800 dark:text-slate-100">
          {kind === "pickup" ? "Pickup Agent" : "Delivery Agent"}
        </h4>
        {block?.user_id && <StatusBadge status={status || "assigned"} />}
      </div>
      {block?.user_id ? (
        <div data-testid={`custody-${kind}-assigned`}
             className="bg-slate-50 dark:bg-slate-700/40 border border-slate-200
                        dark:border-slate-700 rounded-lg p-3 space-y-1.5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-semibold text-sm"
                   data-testid={`custody-${kind}-name`}>{block.name || "—"}</div>
              <div className="text-xs text-slate-500 space-x-2">
                {block.vehicle && <span>{block.vehicle}</span>}
                {block.phone && <span>· {block.phone}</span>}
              </div>
              {block.assigned_at && (
                <div className="text-[10px] text-slate-400">
                  Assigned: {new Date(block.assigned_at).toLocaleString()}
                </div>
              )}
            </div>
            {isLead && !completed && (
              <button onClick={doUnassign} disabled={busy}
                       data-testid={`custody-${kind}-unassign`}
                       className="text-xs px-2 py-1 border border-rose-200
                                  text-rose-700 rounded-lg hover:bg-rose-50
                                  disabled:opacity-50">Unassign</button>
            )}
          </div>
          <div>
            <span className="text-xs font-semibold text-slate-500 uppercase">
              {kind === "pickup" ? "Pickup" : "Delivery"} proof
            </span>
            <div className="mt-1">
              <ProofGallery photos={d?.[`${kind}_proof_photos`] || []}
                             label={`${kind} proof`}
                             testid={`${kind}-proof-gallery`} />
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-500"
             data-testid={`custody-${kind}-unassigned`}>
          No {kind} agent assigned yet.
          {isLead && (
            <button onClick={() => { setOpen(true); fetchAgents(); }}
                    data-testid={`custody-${kind}-assign-btn`}
                    className="ml-2 text-xs font-semibold text-[#FF6B00] hover:underline">
              Assign {kind === "pickup" ? "Pickup" : "Delivery"} Agent
            </button>
          )}
        </div>
      )}
      {open && (
        <>
          <button onClick={() => setOpen(false)} aria-label="Close"
                  className="fixed inset-0 z-40 bg-slate-900/40"
                  data-testid={`custody-${kind}-sheet-backdrop`} />
          <div role="dialog"
               data-testid={`custody-${kind}-sheet`}
               className="fixed inset-x-3 bottom-3 sm:inset-auto sm:top-1/2 sm:left-1/2
                          sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[420px]
                          max-h-[80vh] z-50 bg-white rounded-2xl shadow-2xl flex flex-col">
            <div className="border-b border-slate-200 px-4 py-3 flex items-center gap-2">
              <h3 className="font-semibold flex-1">Select {kind} agent</h3>
              <button onClick={() => setOpen(false)}
                       className="p-1 hover:bg-slate-100 rounded">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {busy && <p className="text-sm text-slate-500 p-4 text-center">Loading…</p>}
              {!busy && agents.length === 0 && (
                <p className="text-sm text-slate-500 p-4 text-center">No agents.</p>
              )}
              {agents.map((a) => (
                <button key={a.user_id} onClick={() => doAssign(a.user_id)}
                        data-testid={`custody-${kind}-agent-${a.user_id}`}
                        className="w-full text-left flex items-center justify-between
                                   px-3 py-3 rounded-lg hover:bg-slate-50 border-b
                                   border-slate-100 min-h-[48px]">
                  <div>
                    <div className="font-medium text-sm">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.vehicle || "—"}</div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100">
                    {a.currently_assigned_tasks_count} active
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function HandoverBox({ d, tid, role, onChanged }) {
  const [files, setFiles] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const status = d?.fulfillment_status;
  const handover = d?.handover || {};
  const photos = d?.handover_proof_photos || [];
  const isLead = role === "micro_hub_lead";
  const sessionFresh = photos.filter(
    (p) => p.uploaded_by_role === "micro_hub_lead"
        && handover.ready_at && p.ts >= handover.ready_at);

  const packingDone = (d?.packing_status === "done")
                      || (d?.packer?.status === "done")
                      || status === "packed" || status === "ready_for_main_hub"
                      || status === "handed_over_to_main_hub"
                      || status === "received_at_main_hub";
  if (!packingDone) {
    return (
      <div data-testid="custody-handover-hidden"
           className="text-xs text-slate-400 italic">
        Handover section appears once packing is done.
      </div>
    );
  }
  const onPick = (e) => setFiles(Array.from(e.target.files || []));
  const reset = () => { setFiles([]); setRemarks("");
                        if (inputRef.current) inputRef.current.value = ""; };

  const markReady = async () => {
    setBusy(true);
    try {
      await api.post(`/micro-hub/orders/${tid}/mark-ready-for-main-hub`);
      toast.success("Marked ready for main hub");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Mark-ready failed");
    } finally { setBusy(false); }
  };
  const uploadProof = async () => {
    if (files.length === 0) { toast.error("Pick at least one photo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));
      if (remarks) fd.append("remarks", remarks);
      await api.post(`/micro-hub/orders/${tid}/handover-proof`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${files.length} photo(s)`);
      reset();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  const confirmHandover = async () => {
    setBusy(true);
    try {
      await api.post(`/micro-hub/orders/${tid}/confirm-handover`,
                      { destination_hub_id: "HUB-BLR01" });
      toast.success("Handover confirmed");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Confirm failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="custody-handover-section" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="font-semibold text-slate-800 dark:text-slate-100">
          Handover to Main Hub
        </h4>
        {status && <StatusBadge status={status} />}
      </div>
      {status === "packed" && isLead && (
        <button onClick={markReady} disabled={busy}
                data-testid="custody-mark-ready-btn"
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold
                           bg-[#FF6B00] hover:bg-[#E55F00] text-white rounded-lg
                           disabled:opacity-50">
          <CheckCircle2 className="h-4 w-4" />
          Mark Ready for Main Hub
        </button>
      )}
      {status === "ready_for_main_hub" && isLead && (
        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200
                        dark:border-slate-700 rounded-lg p-3 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase block">
              Destination
            </label>
            <select disabled data-testid="custody-handover-dest"
                    className="mt-1 w-full sm:w-auto text-sm border border-slate-200
                               rounded-lg px-2 py-2 bg-slate-100">
              <option value="HUB-BLR01">HUB-BLR01 — Bangalore Main Hub</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase block">
              Add handover proof
            </label>
            <input ref={inputRef} type="file" accept="image/*"
                    capture="environment" multiple onChange={onPick}
                    data-testid="custody-handover-file-input"
                    className="mt-1 block w-full text-sm" />
            <textarea value={remarks} maxLength={200}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Optional remarks (200 chars)"
                      data-testid="custody-handover-remarks"
                      className="mt-2 block w-full text-sm border border-slate-200
                                 rounded-lg p-2 min-h-[48px]" />
            <div className="flex gap-2 mt-2">
              <button onClick={uploadProof} disabled={busy || files.length === 0}
                       data-testid="custody-handover-upload-btn"
                       className="inline-flex items-center gap-1 px-3 py-2 text-sm
                                  font-semibold bg-purple-600 hover:bg-purple-700
                                  text-white rounded-lg disabled:opacity-50">
                <Camera className="h-4 w-4" />
                Add Handover Proof
              </button>
              <button onClick={confirmHandover}
                      disabled={busy || sessionFresh.length < 1}
                      title={sessionFresh.length < 1
                              ? "Upload at least one handover proof first" : ""}
                      data-testid="custody-handover-confirm-btn"
                      className="flex-1 inline-flex items-center justify-center gap-1
                                 px-3 py-2 text-sm font-semibold bg-[#FF6B00]
                                 hover:bg-[#E55F00] text-white rounded-lg
                                 disabled:opacity-50">
                <ShieldCheck className="h-4 w-4" />
                Confirm Handover
              </button>
            </div>
          </div>
          <ProofGallery photos={photos} label="handover proof"
                         testid="handover-proof-gallery" />
        </div>
      )}
      {(status === "handed_over_to_main_hub"
          || status === "received_at_main_hub") && (
        <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200
                        dark:border-slate-700 rounded-lg p-3 space-y-2"
             data-testid="custody-handover-readonly">
          <div className="text-xs text-slate-500 space-y-1">
            <div>Destination: <span className="font-mono">{handover.destination_hub_id || "—"}</span></div>
            <div>Handed over: {handover.handed_over_at
                                ? new Date(handover.handed_over_at).toLocaleString() : "—"}</div>
            <div>By: {handover.handed_over_by_name || "—"}</div>
          </div>
          <ProofGallery photos={photos} label="handover proof"
                         testid="handover-proof-gallery" />
        </div>
      )}
    </div>
  );
}

function ReceivedBox({ d, tid, role, onChanged }) {
  const status = d?.fulfillment_status;
  const mhr = d?.main_hub_receive || {};
  const photos = d?.received_proof_photos || [];
  const [files, setFiles] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  if (status !== "handed_over_to_main_hub"
      && status !== "received_at_main_hub") return null;
  const isMHM = role === "main_hub_manager";

  const submit = async () => {
    if (files.length === 0) { toast.error("Pick at least one photo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));
      if (remarks) fd.append("remarks", remarks);
      await api.post(`/hub/inbound-parcels/${tid}/receive`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Parcel received");
      setFiles([]); setRemarks("");
      if (inputRef.current) inputRef.current.value = "";
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Receive failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="custody-received-section" className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h4 className="font-semibold text-slate-800 dark:text-slate-100">
          Main Hub Received
        </h4>
        <StatusBadge status={status === "received_at_main_hub"
                                ? "received_at_main_hub" : "handed_over"} />
      </div>
      {status === "handed_over_to_main_hub" && isMHM && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-2">
          <input ref={inputRef} type="file" accept="image/*"
                  capture="environment" multiple
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  data-testid="custody-receive-file-input"
                  className="block w-full text-sm" />
          <textarea value={remarks} maxLength={200}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional remarks (200 chars)"
                    data-testid="custody-receive-remarks"
                    className="block w-full text-sm border border-slate-200
                               rounded-lg p-2 min-h-[48px]" />
          <button onClick={submit} disabled={busy || files.length === 0}
                   data-testid="custody-receive-submit"
                   className="inline-flex items-center gap-1 px-3 py-2 text-sm
                              font-semibold bg-teal-600 hover:bg-teal-700 text-white
                              rounded-lg disabled:opacity-50">
            <Upload className="h-4 w-4" />
            Receive Parcel
          </button>
        </div>
      )}
      {status === "received_at_main_hub" && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-2"
             data-testid="custody-received-readonly">
          <div className="text-xs text-slate-600 space-y-1">
            <div>Received at: {mhr.received_at
                                ? new Date(mhr.received_at).toLocaleString() : "—"}</div>
            <div>By: {mhr.received_by_name || "—"}</div>
            <div>Main hub: <span className="font-mono">{mhr.received_hub_id || "—"}</span></div>
          </div>
          <ProofGallery photos={photos} label="received proof"
                         testid="received-proof-gallery" />
        </div>
      )}
    </div>
  );
}

function AuditLogBox({ entries }) {
  const [open, setOpen] = useState(false);
  const rows = entries || [];
  return (
    <div data-testid="custody-audit-log" className="border-t border-slate-200 pt-3">
      <button onClick={() => setOpen((v) => !v)}
              data-testid="audit-log-toggle"
              className="w-full flex items-center justify-between text-sm font-semibold
                         text-slate-700 hover:text-slate-900">
        <span>Audit Log (read-only) · {rows.length} entries</span>
        <ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 max-h-[300px] overflow-y-auto"
             data-testid="audit-log-rows">
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500 sticky top-0 bg-white">
              <tr>
                <th className="py-1 pr-2">When</th>
                <th className="py-1 pr-2">Actor</th>
                <th className="py-1 pr-2">Event</th>
                <th className="py-1">Details</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e, i) => (
                <tr key={i} className="border-t border-slate-100"
                     data-testid={`audit-row-${i}`}>
                  <td className="py-1 pr-2 whitespace-nowrap text-slate-500">
                    {e.ts ? new Date(e.ts).toLocaleString() : "—"}
                  </td>
                  <td className="py-1 pr-2">
                    <div className="font-medium">{e.actor_name || "—"}</div>
                    <div className="text-[10px] text-slate-400">{e.actor_role || "—"}</div>
                  </td>
                  <td className="py-1 pr-2 font-mono text-[11px]">{e.event}</td>
                  <td className="py-1 text-slate-500 truncate max-w-[180px]"
                       title={JSON.stringify(e.details || {})}>
                    {e.details ? JSON.stringify(e.details).slice(0, 60) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CustodyTab({ d, tid, role, onChanged }) {
  const auditEntries = useMemo(
    () => (d?.audit_logs || d?.audit_log || []), [d]);
  const status = d?.fulfillment_status;
  const isLead = role === "micro_hub_lead";
  const isMHM = role === "main_hub_manager";
  let footerCTA = null;
  if (isLead && status === "packed") footerCTA = "ready";
  else if (isLead && status === "ready_for_main_hub") footerCTA = "handover";
  else if (isMHM && status === "handed_over_to_main_hub") footerCTA = "receive";

  // MHM-6.D fix · refs let the sticky footer scroll/click the inline action.
  const handoverConfirmRef = useRef(null);
  const receiveFileInputRef = useRef(null);
  const markReadyRef = useRef(null);

  const triggerHandoverConfirm = () => {
    const btn = document.querySelector(
      '[data-testid="custody-handover-confirm-btn"]');
    if (!btn) return;
    btn.scrollIntoView({ behavior: "smooth", block: "center" });
    if (!btn.disabled) btn.click();
  };
  const triggerReceiveModal = () => {
    const btn = document.querySelector('[data-testid="custody-receive-submit"]');
    const input = document.querySelector(
      '[data-testid="custody-receive-file-input"]');
    if (input) input.scrollIntoView({ behavior: "smooth", block: "center" });
    if (btn && !btn.disabled) btn.click();
    else if (input) input.click();
  };
  const triggerMarkReady = async () => {
    try {
      await api.post(`/micro-hub/orders/${tid}/mark-ready-for-main-hub`);
      toast.success("Marked ready for main hub");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.userMessage || e?.response?.data?.detail || "Mark-ready failed");
    }
  };

  return (
    <div className="space-y-4 pb-24 sm:pb-4" data-testid="custody-tab">
      <AgentAssignBox d={d} tid={tid} kind="pickup" role={role} onChanged={onChanged} />
      <div className="border-t border-slate-100" />
      {/* MHM-6.D fix · Packer is now the canonical assign/unassign here.
          The legacy ChainTab block is read-only (PackerAssignment readOnly).*/}
      <div data-testid="custody-packer-section" className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-semibold text-slate-800 dark:text-slate-100">Packer</h4>
          <StatusBadge status={d?.micro_hub?.packing_staff?.packing_status
                                 || d?.packing_status || "pending"} size="sm" />
        </div>
        {d?.micro_hub ? (
          <PackerAssignment tid={tid}
                              mh={d.micro_hub}
                              currentUserRole={role}
                              micro_hub_id={d.micro_hub.hub_id}
                              onChanged={onChanged} />
        ) : (
          <p className="text-sm text-slate-500">Not routed through a micro hub.</p>
        )}
        <div>
          <span className="text-xs font-semibold text-slate-500 uppercase">
            Packing proof
          </span>
          <div className="mt-1">
            <ProofGallery photos={d?.packer_uploads
                                    || d?.packing_proof_photos
                                    || []}
                            label="packing proof"
                            testid="packer-proof-gallery" />
          </div>
        </div>
      </div>
      <div className="border-t border-slate-100" />
      <AgentAssignBox d={d} tid={tid} kind="delivery" role={role} onChanged={onChanged} />
      <div className="border-t border-slate-100" />
      <HandoverBox d={d} tid={tid} role={role} onChanged={onChanged} />
      <div className="border-t border-slate-100" />
      <ReceivedBox d={d} tid={tid} role={role} onChanged={onChanged} />
      <AuditLogBox entries={auditEntries} />

      {footerCTA && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-white
                        border-t border-slate-200 p-3"
             data-testid="custody-sticky-footer">
          {footerCTA === "ready" && (
            <button data-testid="custody-footer-cta" onClick={triggerMarkReady}
                    className="w-full py-3 bg-[#FF6B00] text-white rounded-lg font-semibold">
              Mark Ready for Main Hub
            </button>
          )}
          {footerCTA === "handover" && (
            // MHM-6.D fix · real button, not a text hint. Scrolls to the
            // inline action and (if a session-fresh proof exists already)
            // fires the confirm-handover endpoint directly.
            <button data-testid="custody-footer-cta" onClick={triggerHandoverConfirm}
                    className="w-full py-3 bg-[#FF6B00] text-white rounded-lg font-semibold
                               inline-flex items-center justify-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Confirm Handover
            </button>
          )}
          {footerCTA === "receive" && (
            <button data-testid="custody-footer-cta" onClick={triggerReceiveModal}
                    className="w-full py-3 bg-teal-600 text-white rounded-lg font-semibold
                               inline-flex items-center justify-center gap-2">
              <Upload className="h-4 w-4" />
              Receive Parcel
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ── Main detail surface (shared by drawer + full page) ──────────────

export function OrderDetailBody({ tid, embedded = false, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    if (!tid) return;
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/hub/orders/${tid}/details`);
      setData(data);
    } catch (e) {
      const code = e?.response?.status;
      setError({ code, msg: e.userMessage || e?.response?.data?.detail || "Failed to load order" });
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => { load(); }, [load]);

  const copyTid = () => {
    if (!tid) return;
    navigator.clipboard?.writeText(tid).then(() => toast.success("Copied"));
  };

  return (
    <div className="h-full flex flex-col bg-slate-50" data-testid="order-detail-body">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-4 sm:px-6 py-3 flex items-center gap-3 sticky top-0 z-10">
        {embedded && onClose && (
          <button onClick={onClose} data-testid="order-detail-close"
                  className="p-1.5 hover:bg-slate-100 rounded">
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500">Shipment detail</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold text-slate-800" data-testid="order-detail-tid">{tid}</span>
            <button onClick={copyTid} title="Copy tracking ID" data-testid="order-detail-copy"
                    className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600">
              <Copy className="h-3.5 w-3.5" />
            </button>
            {data && <StatusChip status={data.order.status} label={data.order.current_status_label} />}
          </div>
        </div>
        <button onClick={load} data-testid="order-detail-refresh"
                className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title="Refresh">
          <RefreshCw className={"h-4 w-4 " + (loading ? "animate-spin" : "")} />
        </button>
        {embedded && (
          <Link to={`/dashboard/hub/orders/${tid}`} target="_blank" rel="noopener noreferrer"
                data-testid="order-detail-open-newtab"
                className="text-xs inline-flex items-center gap-1 text-sky-600 hover:underline">
            <ExternalLink className="h-3.5 w-3.5" /> New tab
          </Link>
        )}
      </div>

      {/* Tabs */}
      <div className="px-4 sm:px-6 py-2 border-b border-slate-200 bg-white flex gap-1 overflow-x-auto"
           data-testid="order-detail-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`order-detail-tab-${t.key}`}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${
                    tab === t.key ? "bg-[#FF6B00] text-white" : "text-slate-700 hover:bg-slate-100"
                  }`}>{t.label}</button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
        {loading && (
          <div className="space-y-3" data-testid="order-detail-loading-skeleton">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {!loading && error && (
          <div className="border border-rose-200 bg-rose-50 rounded-xl p-5"
               data-testid="order-detail-error-card">
            <div className="flex items-start gap-3">
              <AlertOctagon className="h-5 w-5 text-rose-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-rose-800">
                  {error.code === 403 ? "Access denied"
                   : error.code === 404 ? "Order not found"
                   : "Couldn't load this order"}
                </h4>
                <p className="text-sm text-rose-700 mt-1">
                  {error.code === 403
                    ? "This order belongs to a hub outside your scope."
                    : error.code === 404
                    ? "We couldn't find this tracking ID."
                    : (typeof error.msg === "string" ? error.msg : "Network error")}
                </p>
                {error.code !== 403 && error.code !== 404 && (
                  <button onClick={load} data-testid="order-detail-retry"
                          className="mt-3 text-sm px-3 py-1.5 border border-rose-300 rounded-lg hover:bg-rose-100">
                    Retry
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {!loading && !error && data && (
          <>
            {(() => {
              const role = (JSON.parse(localStorage.getItem("pickngo_user") || "{}").role
                             || "").toLowerCase();
              return (
                <>
                  {tab === "overview" && <OverviewTab d={data} />}
                  {tab === "custody"  && <CustodyTab d={data} tid={tid} role={role} onChanged={load} />}
                  {tab === "timeline" && <TimelineTab d={data} />}
                  {tab === "chain"    && <ChainTab    d={data} onChanged={load} />}
                  {tab === "courier"  && <CourierTab  d={data} tid={tid} role={role} onChanged={load} />}
                  {tab === "proofs"   && <ProofsTab   d={data} />}
                  {tab === "audit"    && <AuditTab    d={data} />}
                </>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
}

// ── Drawer wrapper ─────────────────────────────────────────────────

export default function OrderDetailDrawer() {
  const { tid, isOpen, close } = useOrderDrawer();
  if (!isOpen) return null;
  return (
    <>
      {/* Backdrop */}
      <button aria-label="Close order detail"
              onClick={close}
              data-testid="order-detail-backdrop"
              className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm" />
      {/* Panel — full screen on mobile, right slide on desktop */}
      <div className="fixed inset-0 sm:inset-y-0 sm:right-0 sm:left-auto sm:w-[720px] z-50
                      bg-white shadow-2xl overflow-hidden flex flex-col"
           role="dialog" aria-modal="true" aria-label="Order detail"
           data-testid="order-detail-drawer">
        <OrderDetailBody tid={tid} embedded onClose={close} />
      </div>
    </>
  );
}
